import { useFieldArray, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { MESSAGES } from "~/resources/Message";
import { FormCloudProvisionerTemplate } from "~/models/Commons ";
import {
  useSaveCloudProvisionerTemplateMutation,
  useUpdateCloudProvisionerTemplateMutation,
} from "~/services/cloudProvisioner";

export const UseFormCloudProvisionerTemplate = (
  onCloseModal: () => void,
  refetch: () => void,
  id?: number
) => {
  const [saveTemplate, { isLoading }] = useSaveCloudProvisionerTemplateMutation();
  const [updateTemplate] = useUpdateCloudProvisionerTemplateMutation();

  const {
    formState: { errors },
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
  } = useForm<FormCloudProvisionerTemplate>({
    defaultValues: {
      fileName: "",
      providerType: "azure",
      osUrnPairs: [],
      azureVmSizes: [],
      azureLocation: "",
      azureResourceGroup: "",
      azureAppPorts: [],
      awsAmiPairs: [],
      awsInstanceTypes: [],
      awsRegion: "",
      awsAppPorts: [],
    },
  });

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "osUrnPairs",
  });

  const { fields: awsAmiFields, append: awsAmiAppend, remove: awsAmiRemove, replace: awsAmiReplace } = useFieldArray({
    control,
    name: "awsAmiPairs",
  });

  const submitForm = async (_data: FormCloudProvisionerTemplate) => {
    try {
      const { osUrnPairs, awsAmiPairs, ...rest } = _data;
      const osImageUrns = Object.fromEntries((osUrnPairs ?? []).map((p) => [p.os, p.urn]));
      const awsAmiIds = Object.fromEntries((awsAmiPairs ?? []).map((p) => [p.os, p.amiId]));
      const response = await saveTemplate({ ...rest, osImageUrns, awsAmiIds }).unwrap();
      if (response.success) {
        toast.success(response?.message);
        refetch();
      } else {
        throw new Error("Save failed");
      }
    } catch (error: any) {
      const title = error?.data?.title || error?.title || MESSAGES.MessageErrorSavingCloudProvisionerTemplate;
      const status = error?.data?.status || error?.status || "";
      toast.error(status ? `Status:${status}\nTitle:${title}` : title);
      console.error("Error saving template", error);
    } finally {
      refetch();
      onCloseModal();
    }
  };

  const updateForm = async (_data: FormCloudProvisionerTemplate) => {
    if (!id) {
      toast.error("Missing template ID for update.");
      return;
    }
    try {
      const { osUrnPairs, awsAmiPairs, ...rest } = _data;
      const osImageUrns = Object.fromEntries((osUrnPairs ?? []).map((p) => [p.os, p.urn]));
      const awsAmiIds = Object.fromEntries((awsAmiPairs ?? []).map((p) => [p.os, p.amiId]));
      const response = await updateTemplate({ id, data: { ...rest, osImageUrns, awsAmiIds } }).unwrap();
      if (response.success) {
        toast.success(response?.message);
        refetch();
      } else {
        throw new Error("Update failed");
      }
    } catch (error: any) {
      const title = error?.data?.title || error?.title || MESSAGES.MessageErrorUpdatingCloudProvisionerTemplate;
      const status = error?.data?.status || error?.status || "";
      toast.error(status ? `Status:${status}\nTitle:${title}` : title);
      console.error("Error updating template", error);
    } finally {
      refetch();
      onCloseModal();
    }
  };

  return {
    handleSubmit,
    control,
    errors,
    submitForm,
    updateForm,
    isLoading,
    reset,
    setValue,
    watch,
    fields,
    append,
    remove,
    replace,
    awsAmiFields,
    awsAmiAppend,
    awsAmiRemove,
    awsAmiReplace,
  };
};
